<script setup lang="ts">
import { router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import { Facet, FacetValue } from './Interfaces/facet.interface'
import Breadcrumbs from '@/Components/Breadcrumbs.vue'
import ApplicationHead from '@/Components/Layout/ApplicationHead.vue'
import { ref, computed, toRef, reactive, inject, onMounted, watch } from 'vue'
import { Dialog, DialogPanel, DialogTitle, Menu, MenuButton, MenuItem, MenuItems, Popover, PopoverButton, PopoverGroup, PopoverPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { ChevronDownIcon } from '@heroicons/vue/20/solid'
import Rating from '@/Components/UI/Rating.vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { ProductVariant } from '@interfaces/index'
import Button from '@/Components/Button.vue'
import LoadingSpinnerIcon from '@/Components/Icons/LoadingSpinnerIcon.vue'
import { Subcategory } from './Interfaces/subcategory.interface'
import { CatalogLayout } from './Interfaces/catalog-layout.interface'
import { useJsonLd } from '@/Composables/json-ld'
import * as Sentry from "@sentry/vue"
import { useTracking } from '@/Composables/tracking'
import Pagination from '@/Components/Pagination.vue'
import ProductVariantCard from '@/Components/UI/ProductVariantCard.vue'
import DialogFilterDisclouser from './Partials/DialogFilterDisclouser.vue'
import DialogFilterTree from './Partials/DialogFilterTree.vue'
import type ziggyRoute from 'ziggy-js'

const props = withDefaults(defineProps<{
    mode: 'catalog' | 'category',
    catalogLayout: CatalogLayout,
    products: JsonResourcePaginated<Array<ProductVariant>>,
    filters: Array<Facet>,
    filtersPredefined: Array<string>,
    type?: string,
    sort?: string,
    parentCategoryId?: number | null,
    parentBrandId?: number | null,
}>(), {
    type: 'catalog',
    sort: 'popular',
    parentCategoryId: null,
    parentBrandId: null,
})

const { t } = useI18n()
const { productVariantsItemListSchema } = useJsonLd()
const { trackProductVariantsListView } = useTracking()
const route = inject<typeof ziggyRoute>('route')!

const products = toRef(props, 'products')
const productsStream = ref<Array<ProductVariant>>(props.products?.data || [])
const facets = ref<Array<Facet>>([])
const showFiltersModal = ref(false)

const selectedSort = ref<string>(props.sort)
const selectedFilters = ref<Array<string>>(props.filtersPredefined || [])

const loading = ref(false)
const loadingPrevious = ref(false)
const loadingMore = ref(false)

const meta = reactive({
    page: {
        from: 1,
        to: 1,
    },
    displayed: 0,
    total: 0,
    progress: 0,
})

const activeFilters = computed(() => {
    const list: Array<{
        key: string,
        facet: Facet,
        value: FacetValue,
    }> = []

    for (const code of selectedFilters.value) {
        const [key, name] = String(code).split(':', 2)
        const facet = facets.value.find((item) => item.key === key)

        if (facet) {
            const value = facet.values.find((item) => {
                if (item.id) {
                    return item.id === parseInt(name)
                }

                return item.name === name
            })

            if (value) {
                list.push({
                    key: code,
                    facet,
                    value,
                })
            }
        }
    }

    return list
})

const hasPrevious = computed(() => {
    return meta.page.from > 1
})

const hasMore = computed(() => {
    return meta.displayed < meta.total
})

const progress = computed(() => {
    if (meta.total === 0) {
        return 0
    }

    return Math.round((meta.displayed / meta.total) * 100)
})

const getFacetName = (facet: Facet) => {
    if (facet.key === 'brand') {
        return t('pages.catalog.brand')
    }

    if (facet.key === 'price_range') {
        return t('pages.catalog.price')
    }

    return facet.name
}

const getFacetValueName = (facet: Facet, value: FacetValue) => {
    if (facet.key === 'price_range') {
        return t(`pages.catalog.price-ranges.${value.name}`)
    }

    return value.name
}

const getFiltersCount = (facet: Facet) => {
    return selectedFilters.value.filter((item) => {
        const [itemKey] = item.split(':', 2)

        return itemKey === facet.key
    }).length
}

const removeFilter = (key: string) => {
    selectedFilters.value = selectedFilters.value.filter((item) => item !== key)
}

const sortOptions = [
    { code: 'popular', name: t('pages.catalog.sort.popular') },
    { code: 'newest', name: t('pages.catalog.sort.new') },
    { code: 'price_high', name: t('pages.catalog.sort.price-high') },
    { code: 'price_low', name: t('pages.catalog.sort.price-low') },
]

const orderedFacets = computed(() => {
    return [...filterFacets.value]
        .filter((item) => [
            'brand',
            'Material', 'Materjal', 'Materiaali', 'Medžiaga', 'Materiāls', 'Materiał', 'Материал',
            'Stone type', 'Kivi tüüp', 'Kivityyppi', 'Akmens tipas', 'Akmens tips', 'Rodzaj kamienia', 'Тип камня',
        ].some((key) => item.key.toLocaleLowerCase().includes(key.toLocaleLowerCase())))
        .sort((a, b) => b.priority - a.priority)
        .slice(0, 3)
})

const filterFacets = computed(() => {
    return [...facets.value].filter((item) => item.key !== 'category')
})

const categoriesFacet = computed(() => {
    const items = [...facets.value].filter((item) => item.key === 'category')

    if (items.length === 0) {
        return null
    }

    return items[0]
})

const reloadProducts = () => {
    loading.value = true

    // Clear products list
    productsStream.value = []

    // Reset meta
    meta.page.from = 1
    meta.page.to = 1
    meta.displayed = 0
    meta.total = 0

    // Refetch data
    loadMore()
}

const getProducts = (page) => {
    const query: any = {
        page,
        sort: selectedSort.value,
        parent_category_id: props.parentCategoryId,
    }

    if (props.type === 'outlet') {
        query.outlet = true
        query.in_stock = true
    }

    if (props.parentBrandId) {
        query.parent_brand_id = props.parentBrandId
    }

    // Apends filters to the query
    const queryFilters: Map<string, Array<string>> = new Map()
    const uriQueryFilters: Record<string, string> = {}

    for (const filter of activeFilters.value) {
        const filterValue = String(filter.value.id || filter.value.name)
        const values = queryFilters.get(filter.facet.key) || []

        if (!values.includes(filterValue)) {
            values.push(filterValue)
        }

        queryFilters.set(filter.facet.key, values)
    }

    if (queryFilters.size > 0) {
        query.filters = {}

        for (const item of queryFilters.entries()) {
            uriQueryFilters[encodeURIComponent(item[0])] = item[1].sort((a: any, b: any) => a - b).join(',')
        }
    }

    return new Promise<{
        variants: typeof props.products,
        facets: typeof props.filters,
    }>((resolve, reject) => {
        const params = route().params

        const url = route(route().current()!, {
            shop_brand: params.shop_brand || undefined,
            shop_categories: params.shop_categories || undefined,
            _query: {
                ...uriQueryFilters,
                filters: query.filters || undefined,
                sort: selectedSort.value !== 'popular' ? selectedSort.value : undefined,
                page: page > 1 ? page : undefined,
            },
        }).replaceAll('%2C', ',')

        router.visit(url, {
            only: ['products', 'filters', 'filtersPredefined', 'layout'],
            preserveScroll: true,
            preserveState: true,
            onSuccess: (page) => {
                const response = page.props.products as typeof props.products

                trackProductVariantsListView(response.data, props.catalogLayout.item_list, {
                    page: response.meta.current_page,
                    perPage: response.meta.per_page,
                })

                resolve({
                    variants: response,
                    facets: page.props.filters as typeof props.filters,
                })
            },
            onError: (error) => {
                reject(error)
            },
        })
    })
}

const loadPrevious = () => {
    loadingPrevious.value = true

    getProducts(meta.page.from - 1)
        .then(({ variants }) => {
            loadingPrevious.value = false

            // Prepend products to the beginning of the list
            productsStream.value = [...variants.data, ...productsStream.value]

            // Update page index
            meta.page.from = variants.meta.current_page
        })
        .catch((error) => {
            console.error(error)

            Sentry.captureException(error)
        })
}

const loadMore = () => {
    loadingMore.value = true

    getProducts(meta.page.to)
        .then(({ variants, facets: facetsData }) => {
            loading.value = false
            loadingMore.value = false

            // Append products to the end of the list
            productsStream.value.push(...variants.data)

            // Set filters
            facets.value = facetsData

            // Meta
            meta.page.to = variants.meta.current_page + 1
            meta.displayed = variants.meta.to || 0
            meta.total = variants.meta.total
        })
        .catch((error) => {
            console.error(error)

            Sentry.captureException(error)
        })
}

watch([selectedFilters, selectedSort], () => {
    reloadProducts()
}, { deep: true })

onMounted(() => {
    facets.value = props.filters

    meta.page.from = props.products.meta.current_page
    meta.page.to = props.products.meta.current_page + 1
    meta.displayed = props.products.meta.to
    meta.total = props.products.meta.total

    trackProductVariantsListView(products.value.data, props.catalogLayout.item_list, {
        page: meta.page.from,
        perPage: props.products.meta.per_page,
    })
})
</script>

<template>
    <div>
        <ApplicationHead
            :title="catalogLayout.title"
            :page="products.meta.current_page"
            :description="catalogLayout.description"
        >
            <meta
                property="og:type"
                content="website"
            >
            <meta
                property="og:image"
                :content="catalogLayout.image_url"
            >

            <component
                :is="'script'"
                type="application/ld+json"
            >
                {{ JSON.stringify(productVariantsItemListSchema(products.data)) }}
            </component>
        </ApplicationHead>

        <Breadcrumbs />

        <div class="bg-white">
            <div class="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
                <h1 class="font-bold text-h4 text-grayscale-900">{{ catalogLayout.title }}</h1>
            </div>
        </div>

        <div class="flex flex-col">
            <div
                v-if="catalogLayout.description"
                class="order-2 bg-primary-800"
            >
                <div class="max-w-6xl px-4 py-12 mx-auto text-center sm:px-6 lg:px-8 text-p2 text-grayscale-100">
                    <p>{{ catalogLayout.description }}</p>
                </div>
            </div>

            <div class="order-1">
                <!-- Filters modal -->
                <TransitionRoot
                    as="template"
                    :show="showFiltersModal"
                >
                    <Dialog
                        as="div"
                        class="relative z-50"
                        aria-labelledby="cart-title"
                        role="dialog"
                        aria-modal="true"
                        @close="showFiltersModal = false"
                    >
                        <TransitionChild
                            as="template"
                            enter="ease-in-out duration-500"
                            enter-from="opacity-0"
                            enter-to="opacity-100"
                            leave="ease-in-out duration-500"
                            leave-from="opacity-100"
                            leave-to="opacity-0"
                        >
                            <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
                        </TransitionChild>

                        <div class="fixed inset-0 z-40 flex">
                            <TransitionChild
                                as="template"
                                enter="transform transition ease-in-out duration-500 sm:duration-700"
                                enter-from="translate-x-full"
                                enter-to="translate-x-0"
                                leave="transform transition ease-in-out duration-500 sm:duration-700"
                                leave-from="translate-x-0"
                                leave-to="translate-x-full"
                            >
                                <DialogPanel class="relative flex flex-col w-full h-full max-w-sm py-6 pb-12 ml-auto overflow-y-auto bg-white shadow-xl">
                                    <div class="flex items-start justify-between px-4">
                                        <DialogTitle class="text-lg font-medium text-slate-900">{{ $t('pages.catalog.filters') }}</DialogTitle>
                                        <div class="flex items-center ml-3 h-7">
                                            <button
                                                type="button"
                                                class="inline-flex items-center justify-center p-2 -m-2 text-gray-400 rounded-md hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                @click="showFiltersModal = false"
                                            >
                                                <span class="sr-only">Close dialog</span>
                                                <XMarkIcon
                                                    class="w-6 h-6 stroke-2"
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        </div>
                                    </div>

                                    <div class="mt-6">
                                        <DialogFilterDisclouser
                                            v-if="categoriesFacet"
                                            :name="$t('pages.catalog.categories')"
                                            :count="getFiltersCount(categoriesFacet)"
                                        >
                                            <div class="space-y-2">
                                                <DialogFilterTree
                                                    v-model="selectedFilters"
                                                    :facet="categoriesFacet"
                                                />
                                            </div>
                                        </DialogFilterDisclouser>

                                        <DialogFilterDisclouser
                                            v-for="facet in filterFacets"
                                            :key="facet.key"
                                            :name="getFacetName(facet)"
                                            :count="getFiltersCount(facet)"
                                        >
                                            <div class="space-y-2">
                                                <DialogFilterTree
                                                    v-model="selectedFilters"
                                                    :facet="facet"
                                                />
                                            </div>
                                        </DialogFilterDisclouser>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </Dialog>
                </TransitionRoot>

                <!-- Filters -->
                <section aria-labelledby="filter-heading">
                    <h2
                        id="filter-heading"
                        class="sr-only"
                    >{{ $t('pages.catalog.filters') }}</h2>

                    <div class="relative z-20 pb-4 bg-white border-b border-grayscale-100">
                        <div class="flex items-center justify-between px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                            <Menu
                                as="div"
                                class="relative inline-block text-left"
                            >
                                <div>
                                    <MenuButton
                                        type="button"
                                        class="inline-flex justify-center text-sm font-medium group text-grayscale-700 hover:text-grayscale-900 focus:outline-none"
                                    >
                                        {{ $t('pages.catalog.sort-by') }}
                                        <ChevronDownIcon
                                            class="flex-shrink-0 w-5 h-5 ml-1 -mr-1 text-grayscale-400 group-hover:text-grayscale-500"
                                            aria-hidden="true"
                                        />
                                    </MenuButton>
                                </div>

                                <transition
                                    enter-active-class="transition duration-100 ease-out"
                                    enter-from-class="transform scale-95 opacity-0"
                                    enter-to-class="transform scale-100 opacity-100"
                                    leave-active-class="transition duration-75 ease-in"
                                    leave-from-class="transform scale-100 opacity-100"
                                    leave-to-class="transform scale-95 opacity-0"
                                >
                                    <MenuItems class="absolute left-0 w-48 mt-2 origin-top-left bg-white rounded-md shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none">
                                        <div class="py-1">
                                            <MenuItem
                                                v-for="option in sortOptions"
                                                :key="option.code"
                                                v-slot="{ active, close }"
                                            >
                                            <a
                                                :href="route(route().current()!, { ...route().params, _query: { sort: option.code, page: 1 } })"
                                                :class="[option.code === selectedSort ? 'font-medium text-gray-900' : 'text-gray-500', active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm outline-none hover:bg-gray-100']"
                                                @click.prevent="selectedSort = option.code; close()"
                                            >
                                                {{ option.name }}
                                            </a>
                                            </MenuItem>
                                        </div>
                                    </MenuItems>
                                </transition>
                            </Menu>

                            <button
                                type="button"
                                class="inline-flex items-center text-sm font-medium group text-grayscale-700 hover:text-grayscale-900 sm:hidden"
                                @click="showFiltersModal = true"
                            >
                                {{ $t('pages.catalog.filters') }}
                                <svg
                                    class="flex-shrink-0 w-4 h-4 ml-2 -mr-1 text-grayscale-400 group-hover:text-grayscale-500"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </button>

                            <div class="hidden sm:block">
                                <div class="flow-root">
                                    <PopoverGroup class="flex items-center -mx-4 divide-x divide-grayscale-100">
                                        <Popover
                                            v-for="facet in orderedFacets"
                                            :key="facet.key"
                                            class="relative inline-block px-4 text-left"
                                        >
                                            <PopoverButton
                                                type="button"
                                                class="inline-flex justify-center text-sm font-medium group text-grayscale-700 hover:text-grayscale-900 focus:outline-none"
                                            >
                                                <span>{{ getFacetName(facet) }}</span>
                                                <span
                                                    v-if="getFiltersCount(facet) > 0"
                                                    class="ml-1.5 rounded py-0.5 px-1.5 bg-gray-200 text-xs font-semibold text-grayscale-700 tabular-nums"
                                                >{{ getFiltersCount(facet) }}</span>
                                                <ChevronDownIcon
                                                    class="flex-shrink-0 w-5 h-5 ml-1 -mr-1 text-grayscale-400 group-hover:text-grayscale-500"
                                                    aria-hidden="true"
                                                />
                                            </PopoverButton>

                                            <transition
                                                enter-active-class="transition duration-100 ease-out"
                                                enter-from-class="transform scale-95 opacity-0"
                                                enter-to-class="transform scale-100 opacity-100"
                                                leave-active-class="transition duration-75 ease-in"
                                                leave-from-class="transform scale-100 opacity-100"
                                                leave-to-class="transform scale-95 opacity-0"
                                            >
                                                <PopoverPanel class="absolute right-0 p-4 mt-2 origin-top-right bg-white rounded-md shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                    <div class="space-y-2">
                                                        <DialogFilterTree
                                                            v-model="selectedFilters"
                                                            :facet="facet"
                                                            origin="popover"
                                                        />
                                                    </div>
                                                </PopoverPanel>
                                            </transition>
                                        </Popover>

                                        <div class="relative inline-block px-4 text-left">
                                            <button
                                                type="button"
                                                class="inline-flex items-center justify-center text-sm font-medium group text-grayscale-700 hover:text-grayscale-900"
                                                @click="showFiltersModal = true"
                                            >
                                                <span>{{ $t('pages.catalog.all-filters') }}</span>
                                                <svg
                                                    class="flex-shrink-0 w-4 h-4 ml-2 -mr-1 text-grayscale-400 group-hover:text-grayscale-500"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                >
                                                    <path
                                                        fill-rule="evenodd"
                                                        d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                                                        clip-rule="evenodd"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    </PopoverGroup>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        v-if="activeFilters.length > 0"
                        class="bg-grayscale-50"
                    >
                        <div class="px-4 py-3 mx-auto max-w-7xl sm:flex sm:items-center sm:px-6 lg:px-8">
                            <h3 class="font-semibold tracking-wide uppercase text-p3 text-grayscale-700">
                                {{ $t('pages.catalog.selected-filters') }}
                            </h3>

                            <div
                                aria-hidden="true"
                                class="hidden w-px h-5 bg-grayscale-100 sm:block sm:ml-4"
                            ></div>

                            <div class="mt-2 sm:mt-0 sm:ml-4">
                                <div class="flex flex-wrap items-center -m-1">
                                    <span
                                        v-for="(filter, filterIdx) in activeFilters"
                                        :key="filterIdx"
                                        class="m-1 inline-flex rounded-full border border-grayscale-100 items-center py-1.5 pl-3 pr-2 text-sm font-medium bg-white text-grayscale-900"
                                    >
                                        <span>{{ getFacetValueName(filter.facet, filter.value) }}</span>
                                        <button
                                            type="button"
                                            class="inline-flex flex-shrink-0 w-4 h-4 p-1 ml-1 rounded-full text-grayscale-500 hover:bg-grayscale-100 hover:text-grayscale-700"
                                            @click.prevent="removeFilter(filter.key)"
                                        >
                                            <span class="sr-only">Remove filter</span>
                                            <svg
                                                class="w-2 h-2"
                                                stroke="currentColor"
                                                fill="none"
                                                viewBox="0 0 8 8"
                                            >
                                                <path
                                                    stroke-linecap="round"
                                                    stroke-width="1.5"
                                                    d="M1 1l6 6m0-6L1 7"
                                                />
                                            </svg>
                                        </button>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Product grid -->
                <section
                    aria-labelledby="products-heading"
                    class="px-4 pt-12 pb-16 mx-auto max-w-7xl sm:pt-16 sm:pb-24 sm:px-6 lg:px-8"
                >
                    <h2
                        id="products-heading"
                        class="sr-only"
                    >{{ $t('pages.catalog.products') }}</h2>
                    <div
                        v-if="hasPrevious"
                        class="flex justify-center max-w-xs mx-auto mb-8"
                    >
                        <Button
                            class="w-full"
                            size="xl"
                            color="primary"
                            loading-label="loading"
                            :loading="loadingPrevious"
                            @click="loadPrevious"
                        >
                            {{ $t('pages.catalog.load-previous') }}
                        </Button>
                    </div>

                    <template v-if="loading">
                        <div class="flex items-center justify-center text-grayscale-700">
                            <LoadingSpinnerIcon class="w-5 h-5 mr-3 -ml-1 animate-spin" />
                            {{ $t('components.button.loading') }}
                        </div>
                    </template>

                    <div
                        id="products-grid"
                        class="grid grid-cols-2 gap-y-10 gap-x-6 md:grid-cols-3 xl:grid-cols-4 xl:gap-x-8"
                    >
                        <ProductVariantCard
                            v-for="(productVariant, index) in productsStream"
                            :key="productVariant.id"
                            :product-variant="productVariant"
                            :item-list="catalogLayout.item_list ? { ...catalogLayout.item_list, index } : undefined"
                        />
                    </div>

                    <Pagination
                        class="hidden mt-10"
                        :paginator="products.meta"
                    />

                    <div
                        v-if="!loading"
                        class="flex flex-col items-center max-w-xs mx-auto mt-16"
                    >
                        <p
                            id="viewed-count"
                            class="text-p2 text-grayscale-500"
                        >{{ $t('pages.catalog.viewed-count', { number: meta.displayed, count: meta.total }) }}</p>

                        <div class="h-0.5 w-full mt-4 bg-grayscale-100 rounded-full">
                            <div
                                class="h-full bg-primary-600"
                                role="progressbar"
                                :style="{ width: progress + '%' }"
                                :aria-valuenow="progress"
                                aria-valuemin="0"
                                aria-valuemax="100"
                                aria-labelledby="viewed-count"
                            ></div>
                        </div>

                        <Button
                            v-if="hasMore"
                            class="w-full mt-12"
                            size="xl"
                            color="primary"
                            loading-label="loading"
                            :loading="loadingMore"
                            @click="loadMore"
                        >
                            {{ $t('pages.catalog.load-more') }}
                        </Button>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>
